import { useEffect, useState } from 'react';
import Masonry from 'react-responsive-masonry';
import { InventoryAsset, SupportCasesItem, WidgetDef } from '../../../types';
import { UpcomingExpirationsWidget } from '../../Compounds/UpcomingExpirationsWidget';
import { SuppliersWidget } from '../../Compounds/SuppliersWidget';
import { StatusWidget } from '../../Compounds/StatusWidget';
import { CasesWidget } from '../../Compounds/CasesWidget';
import { MapWidget } from '../../Compounds/MapWidget';
import { KeyDatesWidget } from '../KeyDatesWidget';
import {
  WidgetKey,
  WidgetPreferences,
  getDefaultPreferences,
  getUser,
  getUserPreferencesSubject,
  isVisibleWidgetByPermissions,
  UserPreferences,
  InventoryGridKeys,
  getCurrentPreferences,
  setPreferences
} from '../../../store/User';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { Project } from '../../../store/Project';
import { ProjectStatusWidget } from '../ProjectStatusWidget';
import { ProjectPipelineWidget } from '../ProjectPipelineWidget';
import { GridFilterItem, GridLogicOperator } from '@mui/x-data-grid-pro';
import { useNavigate } from 'react-router-dom';
import { getColumnsByProduct } from '../UpstackDataGrid/inventoryUtils';

export interface DashboardWidgetsProps {
  inventoryAssets: InventoryAsset[] | [];
  pageTitle: string;
  cases: SupportCasesItem[] | [];
  projects: Project[] | [];
  gridTab?: string;
}

const widgetDefsMap = (page: WidgetKey) => {
  const map = getDefaultPreferences().content.widgets;
  return map[page];
};

export const DashboardWidgets = ({ inventoryAssets, pageTitle, cases, projects, gridTab }: DashboardWidgetsProps) => {
  const [colCount, setColCount] = useState(3);
  const [chartWidth, setChartWidth] = useState(window.innerWidth);
  const lowerTitle = pageTitle.toLocaleLowerCase() as WidgetKey;
  const [widgetSettings, setWidgetSettings] = useState<WidgetPreferences[]>();
  const [widgetDefs, setWidgetDefs] = useState<WidgetDef[]>();
  const navigate = useNavigate();
  const isHome = pageTitle === 'Home';
  const masonryItemClass = isHome ? 'masonry-item' : '';
  const masonryStyle = isHome ? { width: '100%', display: 'block' } : undefined;

  const matchedSetting = (widgetId: string): WidgetPreferences | undefined => {
    return widgetSettings?.find((widget) => widget.widgetId === widgetId);
  };

  useEffect(() => {
    function handlePageResize() {
      const width = window.innerWidth;

      if (width >= 1200) {
        setColCount(3);
        setChartWidth(Number(`${(width - 255) / 3}`));
      } else if (width >= 765) {
        setColCount(2);
        setChartWidth(Number(`${(width - 235) / 2}`));
      } else {
        setColCount(1);
        setChartWidth(Number(`${width - 215}`));
      }
    }
    window.addEventListener('resize', handlePageResize);

    handlePageResize();

    const settingsSub = getUserPreferencesSubject().subscribe((w) => {
      const widgetPrefs = w?.content?.widgets;
      if (widgetPrefs) {
        const widgetPagePrefs = widgetPrefs[lowerTitle];
        setWidgetDefs(widgetDefsMap(lowerTitle));
        setWidgetSettings(widgetPagePrefs);
      }
    });

    return () => {
      window.removeEventListener('resize', handlePageResize);
      if (settingsSub) settingsSub.unsubscribe();
    };
  }, []);

  const filterInventoryAssets = (
    filters: GridFilterItem[],
    logicOperator: GridLogicOperator = GridLogicOperator.And
  ) => {
    const update: UserPreferences = JSON.parse(JSON.stringify(getCurrentPreferences()));
    const tabName = `inventory_grid_${gridTab || 'All'}` as keyof InventoryGridKeys;
    const filterModel = update.content[tabName]?.muiConfig?.filter?.filterModel;
    if (filterModel) {
      // Only store filter items whose field exists in the target tab's column set.
      // Items for unknown columns crash GridFilterForm with
      // "Cannot read properties of undefined (reading 'filterable')" when the filter
      // panel is opened on that tab.
      const tabColumns = getColumnsByProduct(gridTab || 'All');
      const validFields = new Set(tabColumns.map((col) => col.field));
      filterModel.items = filters.filter((f) => validFields.has(f.field));
      filterModel.logicOperator = logicOperator;
      update.content[tabName]!.muiConfig!.filter!.filterModel = filterModel;
    }

    setPreferences(update);
    if (isHome) navigate('/inventory');
  };

  const widgetLists = () => {
    const widgetComponents = {
      upcomingExpirations: (
        <UpcomingExpirationsWidget
          inventoryAssets={inventoryAssets}
          chartWidth={chartWidth}
          isHome={isHome}
          filterInventoryAssets={filterInventoryAssets}
        />
      ),
      status: (
        <StatusWidget
          inventoryAssets={inventoryAssets}
          isHome={isHome}
          filterInventoryAssets={filterInventoryAssets}
        />
      ),
      locations: (
        <MapWidget
          inventoryAssets={inventoryAssets}
          isHome={isHome}
          filterInventoryAssets={filterInventoryAssets}
        />
      ),
      suppliers: (
        <SuppliersWidget
          inventoryAssets={inventoryAssets}
          isHome={isHome}
          filterInventoryAssets={filterInventoryAssets}
        />
      ),
      cases: (
        <CasesWidget
          cases={cases}
          barWidth={chartWidth}
          isHome={isHome}
        />
      ),
      keyDates: (
        <KeyDatesWidget
          projects={projects}
          isHome={isHome}
        />
      ),
      projectStatus: (
        <ProjectStatusWidget
          projects={projects}
          isHome={isHome}
        />
      ),
      projectPipeline: (
        <ProjectPipelineWidget
          projects={projects}
          isHome={isHome}
        />
      )
    };

    return widgetDefs && widgetSettings ? (
      widgetDefs
        .map((def) => matchedSetting(def.widgetId) || def)
        .sort((a, b) => a.index - b.index)
        .filter((widget) => {
          return !widget.disabled && !widget.hide && isVisibleWidgetByPermissions(widget.widgetId, getUser());
        })
        .map((widget) => (
          <div
            className={masonryItemClass}
            style={masonryStyle}
            key={widget.widgetId}>
            {widgetComponents[widget.widgetId]}
          </div>
        ))
    ) : (
      <div className="m-32">
        <ComponentSpinner />
      </div>
    );
  };

  return (
    <>
      {isHome && (
        <Masonry
          columnsCount={colCount}
          gutter="20px">
          {widgetLists()}
        </Masonry>
      )}
      {!isHome && <div className="flex flex-wrap gap-4">{widgetLists()}</div>}
    </>
  );
};
